import { zodResolver } from '@hookform/resolvers/zod';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Control, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import EditIcon from "@mui/icons-material/Edit";
import { EntityWithIdFields } from 'src/types';
import { HttpLCTC } from '../api/httpLCTC';
import {
  OperacionLCTCCuentaLimiteUpdate,
  OperacionLCTCCuentaVista,
  OperacionLCTCLimiteTipo,
  schemaOperacionLCTCCuentaLimiteUpdate
} from '../types/dto';
import { OperacionLCTCCuentaFields, OperacionLCTCCuentaLimiteFields } from "../types/fields";
import { OperacionLCTC } from '../types/types';
import { OperacionLCLTCampoLimite } from './OperacionLCLTCampoLimite';


interface EditableLCTCCampoLimiteProps {
  isEditing: boolean;
  onEdit: () => void;
  value: string | null;
  label: string;
  name: string;
  control: Control<any>;
  loadOptions: () => Promise<OperacionLCTCLimiteTipo[]>;
  onModeChange: (isFreeSolo: boolean) => void;
  disabled?: boolean;
  fullWidth?: boolean;
}

const EditableLCTCCampoLimite = ({
  isEditing,
  onEdit,
  value,
  label,
  name,
  control,
  loadOptions,
  onModeChange,
  disabled,
  fullWidth = true,
}: EditableLCTCCampoLimiteProps) => {
  if (!isEditing) {
    return (
      <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ width: '100%' }}>
        <Typography variant="body2" noWrap>
          {value}
        </Typography>
        <Tooltip title="Editar">
          <span>
            <IconButton onClick={onEdit} size="small" disabled={disabled}>
              <EditIcon fontSize="small" color={disabled ? 'disabled' : 'info'} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <OperacionLCLTCampoLimite
      control={control}
      name={name}
      label={label}
      loadOptions={loadOptions}
      onModeChange={onModeChange}
      buttonType={'icon'}
      disabled={disabled}
      fullWidth={fullWidth}
    />
  );
};


interface OperacionLCTCLimiteFilaProps {
  operacion: OperacionLCTC;
  cuenta: OperacionLCTCCuentaVista;
  loadLimitesOptions: () => Promise<OperacionLCTCLimiteTipo[]>;
  refetch: () => void;
  permiteModificar: boolean,
}

export const OperacionLCTCLimiteFila = ({
  operacion,
  cuenta,
  loadLimitesOptions,
  refetch,
  permiteModificar
}: OperacionLCTCLimiteFilaProps) => {

  if (!permiteModificar) {
    return (
      <TableRow>
        <TableCell>{cuenta[OperacionLCTCCuentaFields.DescClienteTipo]}</TableCell>
        <TableCell>{cuenta[OperacionLCTCCuentaFields.DescClienteSubTipo] || '-'}</TableCell>
        <TableCell align="right">{cuenta[OperacionLCTCCuentaLimiteFields.LimiteConcertacion]}</TableCell>
        {/* <TableCell align="right">{cuenta[OperacionLCTCCuentaLimiteFields.LimiteLiquidacionDVP]}</TableCell>
                <TableCell align="right">{cuenta[OperacionLCTCCuentaLimiteFields.LimiteLiquidacionFOP]}</TableCell> */}
      </TableRow>
    );
  }

  const [isSaving, setIsSaving] = useState(false);
  const [fieldModes, setFieldModes] = useState({
    concertacion: false,
    dvp: false,
    fop: false
  });
  const [editModes, setEditModes] = useState({
    concertacion: false,
    dvp: false,
    fop: false
  });

  const formMethods = useForm<OperacionLCTCCuentaLimiteUpdate>({
    resolver: zodResolver(schemaOperacionLCTCCuentaLimiteUpdate),
    defaultValues: {
      [OperacionLCTCCuentaLimiteFields.LimiteConcertacion]: cuenta[OperacionLCTCCuentaLimiteFields.LimiteConcertacion],
      [OperacionLCTCCuentaLimiteFields.LimiteLiquidacionDVP]: cuenta[OperacionLCTCCuentaLimiteFields.LimiteLiquidacionDVP],
      [OperacionLCTCCuentaLimiteFields.LimiteLiquidacionFOP]: cuenta[OperacionLCTCCuentaLimiteFields.LimiteLiquidacionFOP],
    },
  });

  const { control, handleSubmit, formState: { isDirty }, reset } = formMethods;
  const watchedValues = formMethods.watch(OperacionLCTCCuentaLimiteFields.LimiteConcertacion);

  const handleUpdate: SubmitHandler<OperacionLCTCCuentaLimiteUpdate> = (data) => {
    setIsSaving(true);

    const promise = HttpLCTC.updateLimite(operacion[EntityWithIdFields.Id], cuenta[EntityWithIdFields.Id], data);

    toast.promise(promise, {
      loading: 'Guardando...',
      success: 'Límite actualizado.',
      error: (e) => `Error: ${e}`,
    })
      .then(() => {
        // refetch();
        reset(data);
        setEditModes({ concertacion: false, dvp: false, fop: false });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  useEffect(() => {
    console.log(JSON.stringify(watchedValues))
    if (!isDirty) {
      return;
    }

    // @ts-ignore
    if (Object.values(watchedValues).includes(null)) {
      return;
    }

    const useDebounce = fieldModes.concertacion || fieldModes.dvp || fieldModes.fop;

    if (useDebounce) {
      const timer = setTimeout(() => handleSubmit(handleUpdate)(), 1000);

      return () => clearTimeout(timer);
    } else {
      handleSubmit(handleUpdate)();
    }

  }, [JSON.stringify(watchedValues)]);

  return (
    <TableRow sx={{ '& .MuiFormControl-root': { my: 1 }, }}>
      <TableCell><Typography
        variant="body2">{cuenta[OperacionLCTCCuentaFields.DescClienteTipo]}</Typography></TableCell>
      <TableCell><Typography
        variant="body2">{cuenta[OperacionLCTCCuentaFields.DescClienteSubTipo] || '-'}</Typography></TableCell>
      <TableCell>
        <EditableLCTCCampoLimite
          isEditing={editModes.concertacion}
          onEdit={() => setEditModes(prev => ({ ...prev, concertacion: true }))}
          value={watchedValues}
          label="Límite de concertación (% del AUM)"
          name={OperacionLCTCCuentaLimiteFields.LimiteConcertacion}
          control={control}
          loadOptions={loadLimitesOptions}
          onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, concertacion: isFreeSolo }))}
          disabled={isSaving}
          fullWidth={false}
        />
      </TableCell>
      {/* <TableCell>
                <EditableLCTCCampoLimite
                    isEditing={editModes.dvp}
                    onEdit={() => setEditModes(prev => ({...prev, dvp: true}))}
                    value={watchedValues[OperacionLCTCCuentaLimiteFields.LimiteLiquidacionDVP]}
                    label="Límite de liquidación DVP"
                    name={OperacionLCTCCuentaLimiteFields.LimiteLiquidacionDVP}
                    control={control}
                    loadOptions={loadLimitesOptions}
                    onModeChange={(isFreeSolo) => setFieldModes(prev => ({...prev, dvp: isFreeSolo}))}
                    disabled={isSaving}
                    fullWidth={false}
                />
            </TableCell>
            <TableCell>
                <EditableLCTCCampoLimite
                    isEditing={editModes.fop}
                    onEdit={() => setEditModes(prev => ({...prev, fop: true}))}
                    value={watchedValues[OperacionLCTCCuentaLimiteFields.LimiteLiquidacionFOP]}
                    label="Límite de liquidación FOP"
                    name={OperacionLCTCCuentaLimiteFields.LimiteLiquidacionFOP}
                    control={control}
                    loadOptions={loadLimitesOptions}
                    onModeChange={(isFreeSolo) => setFieldModes(prev => ({...prev, fop: isFreeSolo}))}
                    disabled={isSaving}
                    fullWidth={false}
                />
            </TableCell> */}
    </TableRow>
  );
};
